import React, { useState, useEffect, useRef } from "react";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { EditorState, convertFromRaw, convertToRaw } from "draft-js";
import { stateFromHTML } from "draft-js-import-html";
import { useReactToPrint } from "react-to-print";
import { Button } from "@mui/material";
import { ImageOutlined } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import moment from "moment";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { storage } from "../../../../firebaseConfig";
import sign from "jwt-encode";
import { updateUsageTracker } from "../lib/utils";

/**
 * Fix template formatting by handling escaped characters
 */
const fixTemplate = (rawTemplate) => {
  if (!rawTemplate) return "[Error: Template is missing]";

  let fixedTemplate = rawTemplate
    .replace(/\\"/g, '"') // Fix escaped quotes
    .replace(/\\n/g, "\n"); // Preserve new lines

  if (fixedTemplate.startsWith('"') && fixedTemplate.endsWith('"')) {
    fixedTemplate = fixedTemplate.slice(1, -1);
  }

  return fixedTemplate.trim();
};

const mappedDetails = {
  recipient_name: "Enter Recipient Name",
  patient_name: "Enter Patient Name",
  patient_dob: "Enter Patient DOB",
  clinical_note_fields: "Enter Relevant Medical Information",
  user_name: "Enter User Name",
  user_practitioner_type: "Enter Practitioner Type",
  clinic_name: "Enter Clinic Name",
  clinic_address_1: "Enter Address Line 1",
  clinic_address_2: "Enter Address Line 2",
  clinic_city: "Enter Clinic City",
  clinic_state: "Enter Clinic State",
  clinic_country: "Enter Clinic Country",
  clinic_postcode: "Enter Clinic Postcode",
  clinic_fax: "Enter Clinic Fax",
  user_email: "Enter User Email",
  user_practitioner_number: "Enter Practitioner Number",
  user_ahpra_number: "Enter AHPRA Number",
  cannabis_start_date: "Enter Cannabis Start Date",
  case_reference_number: "Enter Case Reference Number",
  professional_medical_opinion: "Enter Professional Medical Opinion",
  clinic_image: "No Image Available",
  cannabinoid_start_date: "Enter Cannabinoid Start Date",
  patient_age: "Enter Patient Age",
  relevant_medical_conditions: "Enter Relevant Medical Conditions",
  type_of_legal_letter: "Enter Type of Legal Letter",
};

/**
 * Replace placeholders like {{name}} with user-provided values
 */
const replacePlaceholders = (template, userDetails, setMissingFields) => {
  if (!template || !userDetails) return "[Error: Missing Data]";

  let processedTemplate = template;
  const placeholderRegex = /{{(.*?)}}/g;
  let missingFieldsList = [];

  // Extract unique placeholders
  const placeholders = [
    ...new Set(
      template
        .match(placeholderRegex)
        ?.map((ph) => ph.replace(/[{}]/g, "").trim()) || []
    ),
  ];

  // Replace placeholders with actual values and track missing fields
  placeholders.forEach((placeholder) => {
    const fieldKey = placeholder.trim();
    let userValue = userDetails[fieldKey] || null;

    if (!userValue) {
      missingFieldsList.push(mappedDetails[fieldKey]); // Collect missing fields
      processedTemplate = processedTemplate.replace(
        new RegExp(`{{${fieldKey}}}`, "g"),
        `<strong> <i> *****{{${mappedDetails[fieldKey]}}}*****</i> </strong>`
      );
    } else if (fieldKey === "clinical_note_fields") {
      processedTemplate = processedTemplate.replace(
        new RegExp(`{{${fieldKey}}}`, "g"),
        `<p>${userValue}</p>`
      );
    } else {
      processedTemplate = processedTemplate.replace(
        new RegExp(`{{${fieldKey}}}`, "g"),
        `<strong>${userValue}</strong>`
      );
    }
  });

  setMissingFields(missingFieldsList); // Update missing fields state

  // ✅ Convert new lines into proper HTML formatting
  processedTemplate = processedTemplate
    .split("\n\n") // Split paragraphs
    .map(
      (paragraph) =>
        paragraph
          .split("\n") // Handle single-line breaks inside paragraphs
          .map((line) => `<span>${line.trim()}</span>`)
          .join("<br>") // Convert line breaks to `<br>`
    )
    .map((p) => `<p>${p}</p>`) // Wrap each paragraph in `<p>` tags
    .join("");

  return processedTemplate;
};

const LetterTemplateBody = ({
  template,
  userDetails,
  download,
  setDownload,
}) => {
  const [data, setData] = useState(null);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [headerEditorState, setHeaderEditorState] = useState(
    EditorState.createEmpty()
  );
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [missingFields, setMissingFields] = useState([]);
  const editorRef = useRef(null);
  const contentRef = useRef(null);
  const headerRef = useRef(null);
  const clinicTemplate = `
      {{clinic_name}}
      {{clinic_address_1}}
      {{clinic_address_2}}
      {{clinic_city}}, {{clinic_state}}, {{clinic_country}}, {{clinic_postcode}}, {{clinic_fax}}
    `;
  useEffect(() => {
    if (!template?.template || !userDetails) return;

    if (contentRef.current) {
      // ✅ Restore the saved editor state
      setEditorState(
        EditorState.createWithContent(convertFromRaw(contentRef.current))
      );

      return;
    }
    if (headerRef.current) {
      // ✅ Restore the saved editor state
      setHeaderEditorState(
        EditorState.createWithContent(convertFromRaw(headerRef.current))
      );
      return;
    }
    setData(userDetails);
    // Process and clean the template
    const cleanedTemplate = fixTemplate(template.template);
    const processedClinicTemplate = replacePlaceholders(
      clinicTemplate,
      userDetails,
      setMissingFields
    );
    const processedTemplate = replacePlaceholders(
      cleanedTemplate,
      userDetails,
      setMissingFields
    );

    const clinicContentState = stateFromHTML(processedClinicTemplate);
    setHeaderEditorState(EditorState.createWithContent(clinicContentState));
    // Convert processed HTML to Draft.js ContentState
    const contentState = stateFromHTML(processedTemplate);
    setEditorState(EditorState.createWithContent(contentState));

    // ✅ Store initial content in raw format for restoration later
    contentRef.current = convertToRaw(contentState);
  }, [template, userDetails]);

  useEffect(() => {
    if (download) {
      handlePrint();
    }
  }, [download]);
  const handlePrint = useReactToPrint({
    content: () => {
      if (editorRef.current) {
        // Find all toolbars inside editorRef and hide them
        const toolbars = editorRef.current.querySelectorAll(
          ".rdw-editor-toolbar"
        );
        toolbars.forEach((toolbar) => {
          toolbar.style.display = "none";
        });
        const box = editorRef.current.querySelectorAll(".borderBox");
        box.forEach((toolbar) => {
          toolbar.classList.remove("border");
        });
      }

      return editorRef.current;
    },

    documentTitle: template?.template_name || "Letter Template",
    onAfterPrint: async() => {
      // Restore the toolbar after printing
      if (editorRef.current) {
        const toolbars = editorRef.current.querySelectorAll(
          ".rdw-editor-toolbar"
        );
        toolbars.forEach((toolbar) => {
          toolbar.style.display = "flex";
        });
        const box = editorRef.current.querySelectorAll(".borderBox");
        box.forEach((toolbar) => {
          toolbar.classList.add("border");
        });
      }

      const profileData = JSON.parse(localStorage.getItem("profile")); // Parse profileData if it's stored as a string

      const cred = sign(
        { user_id: `${profileData?.UserId}` },
        process.env.REACT_APP_JWT_ENCODE_SECRET || ""
      );
    
      // Update usage tracker for all users (like AUS system)
      const updateUsage = await updateUsageTracker(
        "letter_templates",
        profileData?.UserId,
        cred
      );
    
      setDownload?.(false);
    },
  });

  const handleChangePic = async (event) => {
    try {
      const file = event.target.files[0];
      event.target.value = null; // Reset file input to allow re-upload of same file
      const allowedTypes = ["image/png", "image/jpg", "image/jpeg"];
      if (allowedTypes.includes(file?.type)) {
        setLoading(true);
        const dirName = moment().format("MMMM_YYYY");
        const storageRef = ref(storage, `${dirName}/${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on(
          "state_changed",
          (snapshot) => {
            // Optionally, update progress here
          },
          (error) => {
            console.error("Upload error:", error);
          },
          async () => {
            try {
              const url = await getDownloadURL(uploadTask.snapshot.ref);

              setData((prev) => {
                return {
                  ...prev,
                  clinic_image: url,
                };
              });
              setLoading(false);
            } catch (error) {
              console.error("Error getting download URL:", error);
            }
          }
        );
      } else {
        console.error("Incorrect file format.");
        enqueueSnackbar(
          "File type not accepted. Try PNG, JPEG, or JPG files.",
          { variant: "error" }
        );
      }
    } catch (error) {
      console.error("Error handling file upload:", error);
    }
  };

  return (
    <div className="w-full p-2 p-md-3 py-2 py-md-2 mt-3">
      <div
        style={{
          background: "#fff",
          border: "1px solid #D8D8D8",
          borderRadius: "10px 10px 0px 0px",
        }}
        className="p-4 mt-3 d-flex align-items-center flex-wrap justify-content-between"
      >
        <div>
          <h5>{template?.template_name}</h5>
          {/* <p className="text-muted">Tick a field to add to the letter</p> */}
        </div>
      </div>
      <div
        style={{
          background: "#fff",
          padding: "16px",

          border: "1px solid #D8D8D8",
        }}
        className=" border-top-0 "
      >
        {missingFields.length > 0 && (
          <div
            style={{
              background: "#FFF3CD",
              border: "1px solid #FFC107",
              color: "#856404",
              padding: "12px",
              marginBottom: "15px",
              borderRadius: "5px",
            }}
          >
            <strong>
              ⚠️ Missing Data - You have not given us any data for the following
              fields:
            </strong>
            <br />
            <ul style={{ paddingLeft: "20px", marginTop: "5px" }}>
              {missingFields.map((field, index) => (
                <li key={index}>{field}</li>
              ))}
            </ul>
            <strong>
              <span>Kindly review sections marked </span>{" "}
              <i>{`*****{{XXYYZZ}}*****`}</i>
              <br />
              <span>{`Please also remove any unwanted symbols before downloading.`}</span>
            </strong>
          </div>
        )}

        <div
          className="d-flex flex-column"
          style={{ width: "100%" }}
          ref={editorRef}
        >
          <div className="d-flex justify-content-end">
            {/* <div className=" d-none d-sm-block "></div> */}
            <div className=" d-flex p-3 my-3 border borderBox rounded-3 flex-column">
              <h5 className="rdw-editor-toolbar border-0 fs-5 p-0">
                Letterhead
              </h5>
              {data?.clinic_image ? (
                <img
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                    marginBottom: "10px",
                    borderRadius: "5px",
                    border: "1px solid #D8D8D8",
                    backgroundColor: "rgba(0,0,0,0.07)",
                  }}
                  src={data?.clinic_image || ""}
                  alt={data?.clinic_name}
                />
              ) : (
                <div className="d-flex flex-column gap-3">
                  <Button
                    htmlFor="change-clinic-image"
                    component="label"
                    sx={{
                      backgroundColor: "#F5F5F5",
                      color: "#414141",
                      borderRadius: "5px",
                      padding: "10px 20px",
                      width: "200px",
                      textTransform: "capitalize",
                    }}
                    disabled={loading}
                    loadingPosition="start"
                    loading={loading}
                    startIcon={
                      <ImageOutlined
                        style={{
                          fontSize: "20px",
                          color: "#414141",
                        }}
                      />
                    }
                  >
                    <input
                      id="change-clinic-image"
                      onChange={handleChangePic}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                    />
                    Add Logo
                  </Button>
                </div>
              )}

              <Editor
                editorStyle={{ maxWidth: "350px" }}
                toolbar={{ options: ["inline", "list", "textAlign"] }}
                editorState={headerEditorState}
                onEditorStateChange={setHeaderEditorState}
                wrapperClassName="header-editor-wrapper"
                editorClassName="header-editor-content "
              />
            </div>
          </div>
          <div className="p-2 border borderBox rounded-3">
            <h5 className="px-4 m-0 rdw-editor-toolbar border-0 fs-5 p-0">
              Letter Body
            </h5>
            <Editor
              toolbar={{ options: ["inline", "list", "textAlign"] }}
              editorState={editorState}
              onEditorStateChange={(newState) => {
                setEditorState(newState);
                contentRef.current = convertToRaw(newState.getCurrentContent()); // ✅ Save changes
              }}
              wrapperClassName="editor-wrapper p-4 pt-2 "
              editorClassName="editor-content"
              toolbarClassName="editor-toolbar "
            />
          </div>

          <div
            style={{
              background: "#F2F4F7",
              border: "1px solid #DEE2E6",
            }}
            className="p-3 text-center "
          >
            This letter is auto-generated and doesn't require a signature.
          </div>
        </div>
      </div>
    </div>
  );
};

export default LetterTemplateBody;
